import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  fetchPosts,
  fetchPostsByUser,
  uploadFiles as uploadFilesThunk,
  createPost as createPostThunk,
  updatePost as updatePostThunk,
  deletePost as deletePostThunk,
} from "../redux/thunks/postsThunks";

import {
  selectFeedPosts,
  selectFeedTotal,
  selectFeedStatus,
  selectFeedError,
  selectPostsByUser,
  selectPostsByUserTotal,
  selectPostsByUserStatus,
  selectUploadStatus,
  selectUploadedFiles,
} from "../redux/slices/postsSlice";


/* ================= Utils ================= */
const safeNum = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

export default function usePostsDoctor(
  doctorId,
  {
    autoFetch = true,
    pageSize = 20,
    mode = "feed", // "feed" | "user"
    page = 1,
  } = {}
) {
  const dispatch = useDispatch();
  const isFeed = mode === "feed";

  const posts = useSelector((state) =>
    isFeed ? selectFeedPosts(state) : selectPostsByUser(state, doctorId)
  );

  const total = useSelector((state) =>
    isFeed ? selectFeedTotal(state) : selectPostsByUserTotal(state, doctorId)
  );

  const status = useSelector((state) =>
    isFeed ? selectFeedStatus(state) : selectPostsByUserStatus(state, doctorId)
  );

  const error = useSelector((state) =>
    isFeed
      ? selectFeedError(state)
      : state.posts?.byUser?.[doctorId]?.error || null
  );

  const uploadStatus = useSelector(selectUploadStatus);
  const uploadedFiles = useSelector(selectUploadedFiles);
  const isUploading = uploadStatus === "uploading";

  const hasMore = useMemo(() => {
    const len = Array.isArray(posts) ? posts.length : 0;
    return len < safeNum(total, 0);
  }, [posts, total]);

  const reloadFeed = useCallback(
    (p = 1) => dispatch(fetchPosts({ page: p, pageSize })),
    [dispatch, pageSize]
  );

  const reloadUserPosts = useCallback(
    (p = 1) => {
      if (!doctorId) return Promise.resolve();

      return dispatch(fetchPostsByUser({ userId: doctorId, page: p, pageSize }));
    },
    [dispatch, doctorId, pageSize]
  );
  const reload = isFeed ? reloadFeed : reloadUserPosts;

  // Upload
  const uploadFiles = useCallback(
    (files) => dispatch(uploadFilesThunk(files)),
    [dispatch]
  );

  const createPost = useCallback(
    ({ userId, payload }) => dispatch(createPostThunk({ userId, payload })),
    [dispatch]
  );

  // ✅ UPDATE: maintenant on passe userId (obligatoire côté backend)
  const updatePost = useCallback(
    (postId, userId, data) => dispatch(updatePostThunk({ postId, userId, data })),
    [dispatch]
  );

  const deletePost = useCallback(
    (postId, userId) => dispatch(deletePostThunk({ postId, userId })),
    [dispatch]
  );

  useEffect(() => {
    if (!autoFetch) return;
    if (isFeed) {
      reload(page);
      return;
    }
    if (doctorId) reload(page);
  }, [autoFetch, isFeed, doctorId, reload, page]);

  return {
    posts: Array.isArray(posts) ? posts : [],
    total: safeNum(total, 0),
    status,
    error,
    hasMore,

    reload,

    uploadFiles,
    uploadedFiles,
    isUploading,

    createPost,
    updatePost,
    deletePost,
  };
}